import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

/**
 * SearchabilityNudge — tradesperson dashboard banner that reads the
 * canonical `/api/onboarding/state` payload and lists the exact missing
 * fields that keep the profile out of employer search.
 *
 * Rules:
 *   • Hidden entirely once state.is_searchable === true (never
 *     annoying an active tradesperson).
 *   • Never invents its own completion logic — everything comes from
 *     state.missing_fields + state.missing_field_labels.
 *   • CTA jumps back into the onboarding wizard so the user resumes
 *     exactly where they left off.
 */
export default function SearchabilityNudge() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/onboarding/state`, { withCredentials: true });
        if (!cancelled) setState(res.data);
      } catch {
        /* auth handled elsewhere */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!state || state.user_type !== 'tradesperson') return null;
  if (state.is_searchable) return null;

  const missing = state.missing_fields || [];
  const labels = state.missing_field_labels || missing;
  const codes = state.reason_codes || [];

  const stepsLeft = missing.length || (codes.length ? 1 : 0);
  if (stepsLeft === 0) return null;

  const showRejected = codes.includes('account_rejected') || codes.includes('account_suspended');

  return (
    <div
      className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5 mb-4 shadow-sm"
      data-testid="searchability-nudge"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-semibold text-amber-900" data-testid="searchability-nudge-title">
            {showRejected
              ? 'Your account is currently blocked — please contact support.'
              : `You're ${stepsLeft} step${stepsLeft === 1 ? '' : 's'} from appearing in employer searches`}
          </h3>
          {!showRejected && (
            <p className="text-xs sm:text-sm text-amber-800 mt-1">
              Employers can&apos;t see you until every field below is filled in.
            </p>
          )}
          {labels.length > 0 && !showRejected && (
            <ul
              className="mt-3 space-y-1 text-sm text-amber-900"
              data-testid="searchability-nudge-list"
            >
              {labels.slice(0, 6).map((label, i) => (
                <li
                  key={`${missing[i] || label}-${i}`}
                  className="flex items-center gap-2"
                  data-testid={`searchability-nudge-item-${missing[i] || `item-${i}`}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> {label}
                </li>
              ))}
              {labels.length > 6 && (
                <li className="text-xs text-amber-700 pl-4">
                  + {labels.length - 6} more…
                </li>
              )}
            </ul>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {showRejected ? (
              <a
                href="mailto:support@onsitehand.com?subject=Account%20appeal"
                className="btn-primary text-sm inline-flex items-center gap-1"
                data-testid="searchability-nudge-cta"
              >
                Contact support
                <ChevronRight className="h-4 w-4" />
              </a>
            ) : (
              <a
                href="/onboarding/tradesperson"
                className="btn-primary text-sm inline-flex items-center gap-1"
                data-testid="searchability-nudge-cta"
              >
                Complete profile
                <ChevronRight className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
